import { useState, useEffect } from 'react';
import { useParams, useHistory, useLocation } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import useAPI from '../../../utils/api';
import { CHANNEL_TYPES } from './widgetConstants';

const MAX_IMAGE_SIZE_BYTES = 1 * 1024 * 1024;

export default function useWidgetEditor({ site }) {
  const { siteId, widgetId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const queryWidgetType = new URLSearchParams(location.search).get('widget_type');
  const API = useAPI();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(widgetId !== 'new');
  const [saving, setSaving] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [hoveredChannel, setHoveredChannel] = useState(null);
  const [imageLibrary, setImageLibrary] = useState([]);
  const [fontLibrary, setFontLibrary] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState('content');
  const [validationErrors, setValidationErrors] = useState({});
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [rules, setRules] = useState([]);

  const CHAT_DEFAULTS = {
    view_condition: 'immediately',
    view_condition_value: 5,
    channels: [{ id: Date.now(), type: 'whatsapp', handle: '', label: 'Chat with us' }],
    heading_text: 'Hi there!',
    subheading_text: 'How can we help?',
    show_bubble: true,
    bubble_bg_color: '#FFFFFF',
    bubble_text_color: '#333333',
    menu_header_bg_color: '#ED5A29',
    menu_header_text_color: '#FFFFFF',
    main_bubble_bg_color: '#ED5A29',
    main_bubble_icon_color: '#FFFFFF',
    bubble_icon: 'chat',
    custom_icon_url: '',
    menu_layout: 'card',
  };

  const AI_SUMMARY_DEFAULTS = {
    ai_summary_heading: 'Summarize this article with',
    ai_summary_prompt: 'Summarize the following article in a few bullet points:\n\n{url}',
    ai_summary_providers: ['chatgpt', 'claude'],
    ai_summary_placement: 'css_selector',
    ai_summary_button_style: 'pill',
    ai_summary_button_size: 'medium',
    ai_summary_alignment: 'left',
    ai_summary_open_in_new_tab: true,
    ai_summary_css_selector: '',
    ai_summary_css_position: 'after',
  };

  const initialWidgetType = queryWidgetType || 'chat';
  const [widgetType, setWidgetType] = useState(initialWidgetType);
  const initialSettings = initialWidgetType === 'ai_summary' ? AI_SUMMARY_DEFAULTS : CHAT_DEFAULTS;

  const [widget, setWidget] = useState({
    name: 'My New Widget',
    placement: initialWidgetType === 'ai_summary' ? 'inline' : 'bottom-right',
    settings: initialSettings,
  });

  // Reset activeTab when widget type doesn't support the current tab
  useEffect(() => {
    if (widgetType === 'ai_summary' && activeTab === 'design') {
      setActiveTab('content');
    }
  }, [widgetType, activeTab]);

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    e.target.value = '';
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      enqueueSnackbar('Please upload a valid image file', { variant: 'error' });
      return;
    }
    if (file.size > MAX_IMAGE_SIZE_BYTES) {
      enqueueSnackbar('File size exceeds 1 MB. Please upload a smaller image.', { variant: 'error' });
      return;
    }

    setUploading(true);
    try {
      const [ok, data] = await API.uploadWidgetImage(file);
      if (ok && data && data.url) {
        updateSetting('custom_icon_url', data.url);
        updateSetting('bubble_icon', 'custom');
        // Refresh library after successful upload
        const [libOk, libData] = await API.getWidgetImageLibrary();
        if (libOk && libData.image_uploads) {
          setImageLibrary(libData.image_uploads);
        }
      } else {
        enqueueSnackbar(data?.error || data?.message || 'Upload failed. Please try again.', { variant: 'error' });
      }
    } catch (error) {
      console.error("Upload error:", error);
      enqueueSnackbar('Upload failed. Please try again.', { variant: 'error' });
    } finally {
      setUploading(false);
    }
  };

  const handleImageDelete = async (e, imageId) => {
    e.stopPropagation();
    if (!window.confirm("Are you sure you want to delete this image?")) return;

    // Find the image being deleted to check if it's currently selected
    const imageToDelete = imageLibrary.find(img => img.id === imageId);
    const wasSelected = imageToDelete && widget.settings.custom_icon_url === imageToDelete.url;

    try {
      const [ok] = await API.deleteWidgetImage(imageId);
      if (ok) {
        // Refresh library
        const [libOk, libData] = await API.getWidgetImageLibrary();
        if (libOk && libData.image_uploads) {
          setImageLibrary(libData.image_uploads);
        }

        const newList = (libOk && libData.image_uploads) ? libData.image_uploads : [];

        // If the deleted image was the one being used, clean up
        if (wasSelected) {
          if (newList.length > 0) {
            // Auto-select the first available image
            updateSetting('custom_icon_url', newList[0].url);
          } else {
            // Revert to standard icon mode if no images left
            updateSetting('custom_icon_url', '');
            updateSetting('bubble_icon', 'chat');
          }
        }
      }
    } catch (error) {
      console.error("Delete error:", error);
    }
  };

  useEffect(() => {
    if (widget.settings.font_family && widget.settings.font_family !== 'inherit') {
      // Extract clean font name (e.g., from "Open Sans", sans-serif -> Open Sans)
      const fontName = widget.settings.font_family.split(',')[0].replace(/"/g, '').trim();
      const id = `font-link-${fontName.replace(/\s+/g, '-')}`;
      if (!document.getElementById(id)) {
        const link = document.createElement('link');
        link.id = id;
        link.rel = 'stylesheet';
        link.href = `https://fonts.googleapis.com/css2?family=${fontName.replace(/\s+/g, '+')}:wght@400;500;600;700&display=swap`;
        document.head.appendChild(link);
      }
    }
  }, [widget.settings.font_family]);

  useEffect(() => {
    const fetchData = async () => {
      // Fetch Image Library
      try {
        const [libOk, libData] = await API.getWidgetImageLibrary();
        console.log("Widget Image Library Response:", libData);
        if (libOk && libData.image_uploads) {
          setImageLibrary(libData.image_uploads);
        }
      } catch (e) { console.error("Error fetching library", e); }

      if (widgetId !== 'new') {
        try {
          const [ok, data] = await API.chatWidgets();
          if (ok && data.widgets) {
            const existing = data.widgets.find(w => w.id.toString() === widgetId);
            if (existing) {
              setWidgetType(existing.type === 'Widget::AiSummary' ? 'ai_summary' : 'chat');
              const s = existing.settings || {};
              if (!s.channels) s.channels = [];
              const updatedSettings = {
                ...s,
                heading_text: s.heading_text || 'Hi! How can we help?',
                subheading_text: s.subheading_text || 'How can we help?',
                show_bubble: s.show_bubble !== undefined ? s.show_bubble : (s.show_heading_in_bubble !== undefined ? s.show_heading_in_bubble : true),
                bubble_bg_color: s.bubble_bg_color || '#ED5A29',
                bubble_text_color: s.bubble_text_color || '#ffffff',
                menu_header_bg_color: s.menu_header_bg_color || '#ED5A29',
                menu_header_text_color: s.menu_header_text_color || '#ffffff',
                main_bubble_bg_color: s.main_bubble_bg_color || '#ED5A29',
                main_bubble_icon_color: s.main_bubble_icon_color || '#ffffff',
                bubble_icon: s.bubble_icon || 'chat',
                custom_icon_url: s.custom_icon_url || null,
                channels: s.channels || [],
                ai_summary_heading: s.ai_summary_heading || 'Summarize this article with',
                ai_summary_prompt: s.ai_summary_prompt || 'Summarize the following article in a few bullet points:\n\n{url}',
                ai_summary_providers: s.ai_summary_providers || ['chatgpt', 'claude'],
                ai_summary_placement: s.ai_summary_placement || 'css_selector',
                ai_summary_button_style: s.ai_summary_button_style || 'pill',
                ai_summary_button_size: s.ai_summary_button_size || 'medium',
                ai_summary_alignment: s.ai_summary_alignment || 'left',
                ai_summary_open_in_new_tab: s.ai_summary_open_in_new_tab !== undefined ? s.ai_summary_open_in_new_tab : true,
                ai_summary_css_selector: s.ai_summary_css_selector || '',
                ai_summary_css_position: s.ai_summary_css_position || 'after',
              };
              setWidget({ ...existing, settings: updatedSettings });
            }
          }
        } catch (e) {
          console.error("Error loading widget", e);
        }
      }

      // Fetch Font Library
      try {
        const [ok, data] = await API.getFontLibrary();
        if (ok) setFontLibrary(data);
      } catch (e) {
        console.error("Failed to fetch fonts", e);
      }
      setLoading(false);
    };

    const fetchRules = async () => {
      try {
        const [ok, data] = await API.rules();
        if (ok && Array.isArray(data)) {
          setRules(data);
        }
      } catch (e) {
        console.error("Error loading rules", e);
      }
    };

    fetchData();
    fetchRules();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, widgetId]);

  const updateField = (field, value) => {
    setWidget(prev => ({ ...prev, [field]: value }));
  };

  const updateSetting = (field, value) => {
    setWidget(prev => ({
      ...prev,
      settings: { ...(prev.settings || {}), [field]: value }
    }));
  };

  const addChannel = () => {
    const newChannel = { id: Date.now(), type: 'whatsapp', handle: '', label: 'WhatsApp' };
    updateSetting('channels', [...(widget.settings.channels || []), newChannel]);
  };

  const removeChannel = (id) => {
    updateSetting('channels', widget.settings.channels.filter(c => c.id !== id));
  };

  const updateChannel = (id, field, value) => {
    const newChannels = widget.settings.channels.map(c => {
      if (c.id === id) {
        const updated = { ...c, [field]: value };

        // If type changed, also update the label if it was the default one
        if (field === 'type') {
          const oldTypeData = CHANNEL_TYPES.find(t => t.value === c.type);
          const newTypeData = CHANNEL_TYPES.find(t => t.value === value);

          // If label was either empty or matched the old type's label, update it to the new type's label
          if (!c.label || (oldTypeData && c.label === oldTypeData.label)) {
            updated.label = newTypeData ? newTypeData.label : value;
          }
        }
        return updated;
      }
      return c;
    });
    updateSetting('channels', newChannels);
  };

  const moveChannel = (index, direction) => {
    const newChannels = [...widget.settings.channels];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= newChannels.length) return;

    [newChannels[index], newChannels[newIndex]] = [newChannels[newIndex], newChannels[index]];
    updateSetting('channels', newChannels);
  };

  const saveWidget = async () => {
    // Validation
    const errors = {};

    if (widgetType !== 'ai_summary') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      const phoneRegex = /^\+?[\d\s\-]{7,20}$/;
      const urlRegex = /^(https?:\/\/)?([\da-z\.-]+)\.([a-z\.]{2,6})([\/\w \.-]*)*\/?$/;

      widget.settings.channels.forEach(channel => {
        if (!channel.handle?.trim()) {
          errors[channel.id] = "This field is required";
        } else if (channel.type === 'email' && !emailRegex.test(channel.handle)) {
          errors[channel.id] = "Invalid email address";
        } else if (['phone', 'whatsapp', 'sms'].includes(channel.type) && !phoneRegex.test(channel.handle.replace(/[\s\-\+]/g, ''))) {
          errors[channel.id] = "Invalid phone number";
        } else if (channel.type === 'link' && !urlRegex.test(channel.handle)) {
          errors[channel.id] = "Invalid URL";
        }
      });
    } else {
      if (!widget.settings.ai_summary_css_selector || !widget.settings.ai_summary_css_selector.trim()) {
        errors['css_selector'] = "CSS Selector is required.";
      }
      if (!widget.settings.ai_summary_providers || widget.settings.ai_summary_providers.length === 0) {
        errors['providers'] = "Select at least one AI provider.";
      }
    }

    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      if (widgetType !== 'ai_summary') {
        setActiveTab('channels');
      } else if (errors['css_selector']) {
        setActiveTab('layout');
      } else if (errors['providers']) {
        setActiveTab('channels');
      }
      setShowErrorDialog(true);
      return;
    }

    setValidationErrors({});
    setSaving(true);
    try {
      const payload = {
        ...widget,
        widget_type: widgetType,
        status: 'active',
        settings: {
          ...widget.settings,
          ...(widgetType !== 'ai_summary' ? {
            channels: widget.settings.channels.map(c => ({
              ...c,
              handle: c.type === 'instagram' ? c.handle.replace('@', '') : c.handle
            }))
          } : {})
        }
      };

      if (widgetType === 'ai_summary') {
        payload.placement = 'inline';
      }

      console.log("ChatWidget: Saving payload", payload);
      const [ok, savedWidget] = await API.saveChatWidget(payload);
      if (ok) {
        try {
          window.analytics?.track(widget.id ? 'Widget Updated' : 'Widget Created', {
            widget_id: savedWidget?.widget?.id || savedWidget?.id || widget.id,
            widget_name: widget.name,
            widget_type: widgetType,
            site_id: siteId,
          });
        } catch (_) {}
        history.push(`/sites/${siteId}/widgets`, { success: true, message: `Widget ${widget.id ? 'updated' : 'created'} successfully!` });
      } else if (savedWidget?.status === 401) {
        // auth.handleUnauthorized() (api.js) already cleared the session and is
        // hard-redirecting to /login (DEV-895) — don't also show the generic
        // "try again" alert, which gave no way back in.
      } else {
        alert("Failed to save widget. Please try again.");
      }
    } catch (e) {
      console.error("Save failed", e);
    } finally {
      setSaving(false);
    }
  };

  return {
    // routing
    siteId,
    widgetId,
    history,
    location,
    queryWidgetType,
    widgetType,
    setWidgetType,
    // state
    loading,
    saving,
    previewOpen,
    setPreviewOpen,
    hoveredChannel,
    setHoveredChannel,
    imageLibrary,
    fontLibrary,
    uploading,
    activeTab,
    setActiveTab,
    validationErrors,
    setValidationErrors,
    showErrorDialog,
    setShowErrorDialog,
    rules,
    widget,
    setWidget,
    // handlers
    handleImageUpload,
    handleImageDelete,
    updateField,
    updateSetting,
    addChannel,
    removeChannel,
    updateChannel,
    moveChannel,
    saveWidget,
  };
}
